'use client';

import { useSession } from 'next-auth/react';
import { useLocale } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useContext, useEffect, useState } from 'react';

import { CiHeart, CiSearch, CiShoppingCart, CiUser } from 'react-icons/ci';

import useEscapeKey from '@/app/[locale]/hook/useEscapeKey';
import Register from '@/components/Register';

import type { ELanguage } from '@/enums/language.enum';

import { StoreContext } from '@/components/Store';

import { getUserProfile } from '@/services/ProfileService';
import IconButton from './IconButton';
import SearchBar from './SeacrhBar';
import ShoppingCart from './ShoppingCart';

const Icons = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { loginModeState, registerModalState, profileState, cartState } = useContext(StoreContext);
  const [carts] = cartState;
  const totalItems = carts?.cartItems?.length || 0;
  const [isLoginMode, setIsLoginMode] = loginModeState;
  const [isRegisterModalOpen, setIsRegisterModalOpen] = registerModalState;

  const setProfile = profileState[1];
  const setCarts = cartState[1];

  const router = useRouter();
  const { data: session } = useSession();
  const locale: ELanguage = useLocale() as ELanguage;

  const openRegisterModal = () => {
    setIsRegisterModalOpen(true);
    setIsLoginMode(false);
  };

  const openLoginModal = () => {
    setIsRegisterModalOpen(true);
    setIsLoginMode(true);
  };

  const closeRegisterModal = () => setIsRegisterModalOpen(false);

  const getUserProfileData = async () => {
    try {
      const result = await getUserProfile();
      setProfile(result?.data?.user);
      if (!result?.data?.cart.cartItems?.length) {
        setCarts(result?.data?.cart);
        return;
      }
    } catch (error: any) {
      console.error(error.message);
    }
  };

  useEffect(() => {
    if (session?.user) {
      getUserProfileData();
    } else {
      setProfile(null);
    }
  }, [session]);

  useEscapeKey(() => {
    setIsModalOpen(false);
    setIsSearchActive(false);
    setIsRegisterModalOpen(false);
  });

  return (
    <div className="relative flex items-center gap-4 text-2xl text-black dark:text-white lg:gap-2 2xl:gap-4">
      {session?.user ? (
        <IconButton Icon={CiUser} onClick={() => router.push(`/${locale}/profile`)} />
      ) : (
        <IconButton Icon={CiUser} onClick={openLoginModal} />
      )}
      <Register
        isOpen={isRegisterModalOpen}
        onClose={closeRegisterModal}
        openLogin={openLoginModal}
        openRegister={openRegisterModal}
        isLoginMode={isLoginMode}
      />
      {session?.user && <IconButton Icon={CiHeart} link="/wishlist" />}

      <div className="relative">
        {totalItems > 0 && (
          <span className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-clr-0a text-xs font-semibold text-white dark:bg-white dark:text-black">
            {totalItems}
          </span>
        )}
        <IconButton Icon={CiShoppingCart} onClick={() => setIsModalOpen(true)} />
      </div>

      <IconButton Icon={CiSearch} onClick={() => setIsSearchActive(true)} />
      <SearchBar
        isSearchActive={isSearchActive}
        setIsSearchActive={setIsSearchActive}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />
      <ShoppingCart isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
    </div>
  );
};

export default Icons;
